import { useState } from "react";
import { Container, Row, Col, Modal, Form, Button } from "react-bootstrap";
import format from "date-fns/format";
import { useAcubeService } from "../../service/acube-api.hook";
import { useNavigate } from "react-router-dom";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import { useAppContext } from "../../context/app-context";
import SkeletonScreen from "../../layouts/skeleton";

// ---------- Assets ----------
const like =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-unfilled.svg";
const likeFilled =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-filled.svg";
const share =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/share-icon.svg";
const copyLink =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/link.svg";
const copySuccess =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/copied.svg";

// fallback if env is not defined
const APP_DOMAIN = process.env.REACT_APP_DOMAIN || "https://acubetechnologies.com";

const SearchResults = ({ keyword, blogs: initialBlogs, selectedTags, isLoading }) => {
  const [blogs, setBlogs] = useState(initialBlogs); // ✅ keep local state
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const { postProxy } = useAcubeService();
  const { ipAddress } = useAppContext();
  const navigate = useNavigate();

  // ---------- Modal ----------
  const handleShareClick = (blog) => {
    setCurrentBlog(blog);
    setShowModal(true);
    setIsCopied(false);
  };
  const handleCloseModal = () => setShowModal(false);

  const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

  const handleCopy = () => setIsCopied(true);

  // ---------- Helpers ----------
  const formatDate = (dateString) => format(new Date(dateString), "d MMMM yyyy");

  const handleCategoryClick = (type) => {
    const formattedType = type.trim().replace(/\s+/g, "-").toLowerCase();
    navigate(`/blogs/category/${formattedType}`);
  };

  // ---------- Like Toggle ----------
  const handleLikeClick = async (blog) => {
    try {
      const alreadyLiked = blog.likes && blog.likes.length > 0;

      await postProxy(`blog/like`, {
        blogUUID: blog.blog_uuid,
        ip: ipAddress,
      });

      setBlogs((prevBlogs) =>
        prevBlogs.map((b) =>
          b.blog_uuid === blog.blog_uuid
            ? {
              ...b,
              likesAdded: alreadyLiked ? Math.max(0, b.likesAdded - 1) : b.likesAdded + 1,
              likes: alreadyLiked ? [] : [{}],
            }
            : b,
        ),
      );
    } catch (err) {
      console.log("Error toggling like:", err);
    }
  };

  const addViewsCount = async (uuid) => {
    const storageKey = `blog-view-${uuid}`;

    // Already counted in this browser
    const hasViewed = localStorage.getItem(storageKey);

    if (hasViewed) {
      return;
    }

    try {
      await postProxy("blog/views", { blogUUID: uuid });

      // Mark as viewed
      localStorage.setItem(storageKey, "true");
    } catch (error) {
      console.error("Error adding view count:", error);
    }
  };

  const handleBlogClick = async (blog) => {
    await addViewsCount(blog.blog_uuid);
    navigate(`/blogs/${blog.slug}`);
  };

  return (
    <section className="search-results">
      <Container fluid className="py-3">
        {selectedTags.length === 0 && (
          <h2 className="ft-wt-700 mb-4">Search Results for "{keyword}"</h2>
        )}
        <Row className="blog-cards">
          {isLoading ? (
            <SkeletonScreen count={6} />
          ) : blogs.length > 0 ? (
            blogs.map((blog) => (
              <Col
                xs="12"
                md="6"
                lg="4"
                key={blog.id || blog.blog_uuid}
                className="mb-4 blog-card cursor-pointer"
              >
                <img
                  src={blog.template_image}
                  alt={`Thumbnail for ${blog.title}`}
                  className="blog-thumbnail"
                  loading="lazy"
                  onClick={() => handleBlogClick(blog)}
                />
                <div className="blog-card-info">
                  <div className="blog-meta">
                    <span
                      className="author"
                      onClick={() => handleCategoryClick(blog.service_category)}
                    >
                      {blog.service_category}
                    </span>
                    <span className="date">{formatDate(blog.created_at)}</span>
                  </div>
                  <h3 className="blog-card-title" onClick={() => handleBlogClick(blog)}>
                    {blog.title}
                  </h3>
                  <p className="blog-content" onClick={() => handleBlogClick(blog)}>
                    {blog.template_content}
                  </p>
                  <div className="blog-actions d-flex justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={blog?.likes?.length ? likeFilled : like}
                        alt="Like this blog"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeClick(blog);
                        }}
                      />
                      <img
                        src={share}
                        alt="Share this blog"
                        style={{ width: "16px" }}
                        className="cursor-pointer"
                        onClick={() => handleShareClick(blog)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">{blog.likesAdded} likes</h6>
                      <h6 className="mb-0">{blog.views} views</h6>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : keyword ? (
            <Col>
              <p>No results found for "{keyword}".</p>
            </Col>
          ) : (
            <Col>
              <p>No results found.</p>
            </Col>
          )}
        </Row>
      </Container>

      {/* 📤 Share Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title className="ft-wt-700">Share This Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 text-center">
              <Form.Label>Share to</Form.Label>
              <div className="d-flex gap-2 justify-content-center">
                <FacebookShareButton url={shareURL}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareURL}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <LinkedinShareButton url={shareURL}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <EmailShareButton url={shareURL}>
                  <EmailIcon size={48} round />
                </EmailShareButton>
              </div>
            </Form.Group>

            <Form.Group className="mb-3 position-relative">
              <Form.Label>Or copy the link</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={shareURL} readOnly />
                <CopyToClipboard text={shareURL} onCopy={handleCopy}>
                  <Button className="ms-2 p-0 copy-btn" variant="link">
                    <img
                      src={isCopied ? copySuccess : copyLink}
                      alt={isCopied ? "copied" : "copy"}
                      className="cursor-pointer"
                    />
                  </Button>
                </CopyToClipboard>
              </div>
              {isCopied && <div className="link-success-msg">Link copied successfully!</div>}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="ft-wt-700 text-primary text-decoration-none"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default SearchResults;
